import Component from 'vue-class-component';
import { Mixins, Prop, Ref } from 'vue-property-decorator';
import { Section } from '@/courses/models/section.model';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { required, maxLength} from 'vuelidate/lib/validators';
import { Status } from '@/models/status.model';
import { CreateObjectsMixin } from '@/courses/mixins/create-objects.mixin'
import Course from '@/content/models/course.model';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import ToolService from '@/providers/tools';

@Component({
  name: 'ed-create-subdeck-object',
  components: {
    EdDeckCard: () => import(/* EdDeckCard */'../ed-deck-card/ed-deck-card.component.vue'),
    EdSelectComponentCourse: () => import(/* EdSelectComponentCourse */ '@/course-templates/components/ed-select-component-course/ed-select-component-course.component.vue'),
    EdSelectTag: () => import(/* EdSelectTag */'@/courses/components/ed-select-tag/ed-select-tag.component.vue')
  }
})
export default class EdCreateSubdeckObject extends Mixins(CreateObjectsMixin) {

  @Prop() readonly section: Section;
  @Prop() readonly courseId: number;

  isSaving: boolean = false;
  status: Status = new Status();
  randomCards: boolean = false;
  randomView: boolean = false;
  selectedDeck: Section = null;
  selectedCards: number[] = [];
  cards: Section[] = [];
  randomIndexes = new Set();
  totalCards = EdInputModel('totalCards', 1);
  cardToGo = EdInputModel('cardToGo', '');
  cardsFocused: string[] = [];
  isMobile = false;
  tags: Array<string> = ['TEST', 'CLASSWORK', 'HOMEWORK'];
  tagSelected: string = '';
  private readonly listItemHeight: number = 37

  @Ref('listContainer')
  public listContainer!: Vue;

  get editionMode() {
    return this.params.edition;
  }

  get hasSubDeck() {
    if(!this.selectedDeck) {
      return false;
    }

    if(!this.selectedCards || this.selectedCards.length === 0) {
      return false;
    }
    
    return true;
  }

  get course(): Course {
    return this.getCourseFromStore({courseId: this.params.courseId , classId: this.params.classId });
  }

  get decks() {
    return this.course.deckLibrary.sections;
  }
  
  get showCards() {
    return this.cards.length > 0;
  }

  get allowRandom() {
    return this.maxCards > 1;
  }

  get maxCards() {
    return this.selectedDeck ? this.selectedDeck.sections.length : 0;
  }

  get isFormInvalid() {
    return !this.form.name.value || this.form.deckId.value === 0 || !this.form.name.valid;
  }

  get canSave() {
    return this.hasSubDeck && !this.isSaving && !this.isRandomCountInvalid && !this.isFormInvalid;
  }

  get isRandomCountInvalid() {
    if (!this.randomCards) {
      return false;
    }

    const countCards = Number(this.totalCards.value);
    const isNotANumber = isNaN(countCards);
    const isGreaterThanMax = countCards > this.maxCards;
    const isLessThanMin = countCards <= 0;

    return isNotANumber || isGreaterThanMax || isLessThanMin;
  }

  get isTemplate() {
    return this.params.contentType === 'TEMPLATE';
  }

  longCardName(cardName: string) {
    return cardName?.length > 40;
  }

  onCardListScroll() {
    document.querySelectorAll('.menu-container').forEach(
      menu => menu['style'].display = 'none'
    );
  }

  setDeck(deckId) {
    this.selectedDeck = this.course.deckLibrary.sections.find(s => {
      return s.sectionId === deckId;
    });
    // this.randomIndexes.clear();

    if(this.selectedDeck){
      this.cards = this.selectedDeck.sections;
      this.totalCards.value = Math.floor(this.selectedDeck.sections.length / 2); //by default half cards as random
    } else {
      this.cards = [];
    }
    this.randomCards = false;
    this.randomView = false;
    this.selectedCards = [];

    if(this.selectedDeck.component) {
      this.componentSelected = this.components.find(c => c.id == this.selectedDeck.component.id);
    } else if(this.components) {
      this.componentSelected = this.components.find(c => c.mandatory);
    }
  }

  getRandomIndex() {
    let min = 0;
    let max = Math.floor(this.maxCards);
    return Math.floor(Math.random() * (max - min) + min);
  }

  randomizeCards() {
    const countCards = Number(this.totalCards.value);

    if (this.isRandomCountInvalid) {
      return;
    }

    this.randomIndexes.clear(); 

    while (this.randomIndexes.size < countCards) {
      let index = this.getRandomIndex();
      let cardId = this.cards[index];
      if(!this.randomIndexes.has(cardId)){
        this.randomIndexes.add(index);
      }
    }

    this.selectedCards = [];
    this.cards = [];
    for(var i = 0; i < this.selectedDeck.sections.length; i++){
      if(this.randomIndexes.has(i)){
        this.cards.push(this.selectedDeck.sections[i])
        this.selectedCards.push(this.selectedDeck.sections[i].id)
      }
    }
  }
  
  changeSelectMode() {
    if(this.randomCards) {
      this.randomizeCards();
     } else {
      this.selectedCards = [];
      this.cards = this.selectedDeck.sections;
    }
  }

  getImageURL(section){
    if(section && section.assetsInfo[0]){
      return section.assetsInfo[0].thumbnailSmallUrl ? section.assetsInfo[0].thumbnailSmallUrl : section.assetsInfo[0].url
    }
    return '';
  }

  save(){
    if(this.editionMode) {
      this.updateSubDeck();
    } else {
      this.saveSubDeck();
    }
  }
  
  async saveSubDeck() {
    try {
      this.isSaving = true;
      let id = await this.$Courses.Store.createSubDeckA( {
        contentId: this.params.courseId,
        classId: this.params.classId,
        parentID: this.params.parentId,
        deckId: this.form.deckId.value,
        name: this.form.name.value,
        selectMode: this.randomCards ? 'RANDOM' : 'EXACT',
        userViewType: this.randomView ? 'RANDOM' : 'EXACT',
        cards: this.selectedCards,
        labelSections: this.tagSelected
      });
      await this.refreshContent({
        sectionId: id,
        courseId: this.params.courseId,
        classId: this.params.classId,
        parentId: this.params.parentId,
      });
      this.clear();
      this.close();
      this.isSaving = false;
    } catch(err) {
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.errors.errorSavingObject')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
      this.isSaving = false;
    }
  }

  async updateSubDeck() {
    try {
      this.isSaving = true;
       await this.$Courses.Store.updateSubDeckA( {
        contentId: this.params.courseId,
        classId: this.params.classId,
        parentID: this.params.parentId,
        subDeck: this.params.section,
        name: this.form.name.value,
        userViewType: this.randomView ? 'RANDOM' : 'EXACT',
        labelSections: this.tagSelected,
        selectMode: this.randomCards ? 'RANDOM' : 'EXACT',
        cards: this.selectedCards
      });
      
      await this.refreshContent({
        sectionId: this.params.section.id,
        courseId: this.params.courseId,
        classId: this.params.classId,
        parentId: this.params.parentId,
      });

      this.clear();
      this.close();
      this.isSaving = false;

    } catch(err) {
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.errors.errorSavingObject')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
      this.isSaving = false;
    }
  }

  clear() {
    this.form.set('name', '');
    this.form.set('deckId','');
    this.randomCards = false;
    this.randomView = false;
    this.selectedDeck = null;
    this.selectedCards = [];
    this.cards = [];
    this.randomIndexes.clear()
  }

  close() {
    this.$EdModal.closeModal();
  }

  init(deckId: number = 0, name: string = '' ) {
    this.form = EdFormModel([
      // EdInputModel('parentId', this.params.parentId, {required}, this.params.parentId > 0 ? true : false),
      EdInputModel('deckId', deckId, {required}, deckId > 0 ? true : false),
      EdInputModel('name', name, {required,maxLength: maxLength(60)}, name ? true : false),
    ]);
  }

  setData() {
    this.status.setLoading();
    if(this.editionMode) {
      let data =  JSON.parse(this.params.section.text);

      this.selectedDeck = this.course.deckLibrary.sections.find(s => {
        return s.sectionId === data.deck;
      });
      if(this.selectedDeck){
        this.randomCards = data.selectMode === 'RANDOM';
        this.randomView = data.userView === 'RANDOM';
        this.selectedCards = data.cards;
        if(this.randomCards) {
          let currentCards = new Set(data.cards)
          this.selectedDeck.sections.forEach((card) => {
            if(currentCards.has(card.id))
              this.cards.push(card);
          });
          this.totalCards.value = this.cards.length;
        } else  { 
          this.cards = this.selectedDeck.sections; 
          this.totalCards.value = Math.floor(this.selectedDeck.sections.length / 2);
        }
      }
      this.init(data.deck, this.params.section.name);

      this.tagSelected = this.params.section.labelSections;
      if(this.params.section.component) {
        this.componentSelected = this.components.find(c => c.id == this.params.section.component.id);
      } else if(this.components) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    } else {
      this.init();
      if(this.course.eduacComponents) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    }
    this.status.setLoad();
  }

  goToCard() {
    this.cardsFocused = [];
    let cardNameToGo = this.cardToGo.value.toLowerCase();

    if(!cardNameToGo) return
  
    const matchedCards = this.cards
      .filter(card => card.name.toLowerCase().includes(cardNameToGo))
      .map(card => card.name);
  
    if (matchedCards.length === 0) {
      return;
    }
  
    const firstIndex = this.cards.findIndex(card => card.name === matchedCards[0]);
    this.listContainer.$el.scrollTo({ top: firstIndex * this.listItemHeight });
  
    this.cardsFocused = matchedCards;
  }

  created() {
    this.setData();
  }

  mounted() {
    this.isMobile = ToolService.getIsMobileDevice();
  }

}